import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, Table, Select, InputNumber, Button, Space, Row, Col, Typography,
  Tag, message, Alert, Divider, Tooltip, Statistic
} from 'antd';
import {
  SearchOutlined, PlusOutlined, DeleteOutlined, DownloadOutlined,
  SafetyCertificateOutlined, WarningOutlined
} from '@ant-design/icons';
import { securityAPI, authAPI, itemAPI } from '../services/api';

const { Text, Title } = Typography;
const { Option, OptGroup } = Select;

// 置顶三币(名称 + 编号,幸运币两个必须靠编号区分)
const PINNED_ITEMS = [
  { gid: 67174445, name: 'AOZ', scope: 'global' },
  { gid: 67174446, name: '免费AOZ', scope: 'global' },
  { gid: 67174447, name: 'GM AOZ', scope: 'global' },
  { gid: 67174411, name: '黄金', scope: 'global' },
  { gid: 67174413, name: '免费黄金', scope: 'global' },
  { gid: 67174426, name: '幸运币(全局)', scope: 'global' },
  { gid: 67240300, name: '幸运币(背包)', scope: 'item' }
];

// 全局资源 gid(前端提示用;后端为权威判定)
const GLOBAL_GIDS = new Set([67174445, 67174446, 67174447, 67174411, 67174413, 67174426]);

let rowSeq = 0;
function newRow() {
  rowSeq += 1;
  return { key: `row-${rowSeq}`, gid: undefined, level: undefined, min: undefined };
}

function CheatAccountScan() {
  const [zones, setZones] = useState([]);
  const [serverId, setServerId] = useState(undefined);
  const [thresholds, setThresholds] = useState([newRow()]);
  const [rechargeMax, setRechargeMax] = useState(undefined);
  const [aozConsumeMin, setAozConsumeMin] = useState(undefined);
  const [aozConsumeMax, setAozConsumeMax] = useState(undefined);
  const [itemOptions, setItemOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // 加载区服列表
  useEffect(() => {
    (async () => {
      try {
        const resp = await authAPI.getZones();
        if (resp.data && resp.data.success) {
          const list = resp.data.data.zones || [];
          // 按 serverId 去重
          const seen = new Map();
          list.forEach((z) => {
            const sid = Number(z.serverId);
            if (Number.isFinite(sid) && sid > 0 && !seen.has(sid)) {
              seen.set(sid, { serverId: sid, name: z.label || z.name || `区服${sid}` });
            }
          });
          const zoneList = [...seen.values()].sort((a, b) => a.serverId - b.serverId);
          setZones(zoneList);
          if (zoneList.length > 0) {
            setServerId(zoneList[0].serverId);
          }
        }
      } catch (e) {
        console.error('加载区服失败', e);
        message.error('加载区服列表失败');
      }
    })();
  }, []);

  // 道具搜索(复用 itemAPI.getList)
  const searchItems = useCallback(async (keyword) => {
    if (!keyword || String(keyword).trim().length === 0) {
      setItemOptions([]);
      return;
    }
    setSearching(true);
    try {
      const resp = await itemAPI.getList({ keyword: String(keyword).trim(), page: 1, limit: 50 });
      if (resp.data && resp.data.success) {
        const items = (resp.data.data.items || []).map((it) => ({
          gid: Number(it.item_id),
          name: it.name || String(it.item_id)
        })).filter((it) => Number.isFinite(it.gid));
        setItemOptions(items);
      }
    } catch (e) {
      console.error('搜索道具失败', e);
    } finally {
      setSearching(false);
    }
  }, []);

  const updateRow = (key, patch) => {
    setThresholds((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const addRow = () => setThresholds((rows) => [...rows, newRow()]);

  const removeRow = (key) => {
    setThresholds((rows) => (rows.length <= 1 ? rows : rows.filter((r) => r.key !== key)));
  };

  const hasGlobalSelected = useMemo(
    () => thresholds.some((r) => r.gid && GLOBAL_GIDS.has(Number(r.gid))),
    [thresholds]
  );

  const runScan = async () => {
    if (!serverId) {
      message.warning('请选择区服');
      return;
    }
    const cleaned = [];
    for (let i = 0; i < thresholds.length; i += 1) {
      const r = thresholds[i];
      if (r.gid === undefined || r.gid === null || r.gid === '') continue;
      const gid = Number(r.gid);
      const min = Number(r.min);
      if (!Number.isFinite(gid) || gid <= 0) {
        message.warning(`第 ${i + 1} 行 gid 非法`);
        return;
      }
      if (!Number.isFinite(min) || min <= 0) {
        message.warning(`第 ${i + 1} 行阈值必须为正整数`);
        return;
      }
      const row = { gid, min };
      if (r.level !== undefined && r.level !== null && r.level !== '') {
        row.level = Number(r.level);
      }
      cleaned.push(row);
    }
    if (cleaned.length === 0) {
      message.warning('请至少配置一条有效的道具阈值');
      return;
    }

    const payload = { serverId, itemThresholds: cleaned };
    if (rechargeMax !== undefined && rechargeMax !== null && rechargeMax !== '') {
      payload.rechargeMax = Number(rechargeMax);
    }
    const hasAozMin = aozConsumeMin !== undefined && aozConsumeMin !== null && aozConsumeMin !== '';
    const hasAozMax = aozConsumeMax !== undefined && aozConsumeMax !== null && aozConsumeMax !== '';
    if (hasAozMin) payload.aozConsumeMin = Number(aozConsumeMin);
    if (hasAozMax) payload.aozConsumeMax = Number(aozConsumeMax);
    if (hasAozMin && hasAozMax && Number(aozConsumeMax) < Number(aozConsumeMin)) {
      message.warning('累计消耗AOZ币的最大值不能小于最小值');
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      const resp = await securityAPI.cheatScan(payload);
      if (resp.data && resp.data.success) {
        setResult(resp.data.data);
        message.success(`扫描完成,命中 ${resp.data.data.total} 个账号`);
      } else {
        message.error((resp.data && resp.data.message) || '扫描失败');
      }
    } catch (e) {
      const msg = e.response && e.response.data && e.response.data.message;
      message.error(msg || '扫描失败');
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    if (!result || !result.results || result.results.length === 0) {
      message.warning('无数据可导出');
      return;
    }
    const usedThresholds = result.itemThresholds || [];
    const headers = ['游戏ID', '角色名', '等级', '累充', '累计消耗AOZ币'];
    usedThresholds.forEach((t) => {
      const lvl = (t.level !== null && t.level !== undefined) ? `-Lv${t.level}` : '';
      headers.push(`gid${t.gid}${lvl}`);
    });
    const escape = (v) => {
      const s = v === null || v === undefined ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.map(escape).join(',')];
    result.results.forEach((row) => {
      const cells = [row.user_id, row.name, row.level, row.recharge_total, row.aoz_consume];
      (row.items || []).forEach((it) => cells.push(it.amount));
      lines.push(cells.map(escape).join(','));
    });
    const csv = '﻿' + lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cheat_scan_server${result.serverId}_${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const resultColumns = useMemo(() => {
    const base = [
      { title: '游戏ID', dataIndex: 'user_id', key: 'user_id', width: 160, fixed: 'left' },
      { title: '角色名', dataIndex: 'name', key: 'name', width: 160, ellipsis: true },
      { title: '等级', dataIndex: 'level', key: 'level', width: 80 },
      {
        title: '累充',
        dataIndex: 'recharge_total',
        key: 'recharge_total',
        width: 110,
        render: (v) => <Text type="danger">{Number(v || 0).toLocaleString()}</Text>
      },
      {
        title: (
          <Tooltip title="每日凌晨定时统计(非实时),统计口径为近30天窗口">
            <span>累计消耗AOZ币</span>
          </Tooltip>
        ),
        dataIndex: 'aoz_consume',
        key: 'aoz_consume',
        width: 150,
        render: (v) => <Text>{Number(v || 0).toLocaleString()}</Text>
      }
    ];
    const usedThresholds = (result && result.itemThresholds) || [];
    usedThresholds.forEach((t, idx) => {
      const lvl = (t.level !== null && t.level !== undefined) ? ` Lv${t.level}` : '';
      base.push({
        title: (
          <Tooltip title={`gid ${t.gid} 阈值≥${t.min}`}>
            <span>gid{t.gid}{lvl}</span>
          </Tooltip>
        ),
        key: `th-${idx}`,
        width: 130,
        render: (_, record) => {
          const cell = (record.items || [])[idx];
          const amount = cell ? cell.amount : 0;
          return <Text strong>{Number(amount || 0).toLocaleString()}</Text>;
        }
      });
    });
    return base;
  }, [result]);

  return (
    <div style={{ padding: 16 }}>
      <Title level={4}>
        <SafetyCertificateOutlined /> 作弊账号扫描
      </Title>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="纯只读扫描:按区服 + 多道具数量阈值(交集)+ 累充上限筛选可疑账号。不产生任何写操作。"
        description="幸运币有两个编号:67174426(全局)与 67240300(背包),请按编号区分。结果最多返回 2000 条。"
      />

      <Card title="扫描条件" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <div style={{ marginBottom: 4 }}><Text strong>区服</Text></div>
            <Select
              style={{ width: '100%' }}
              placeholder="选择区服"
              value={serverId}
              onChange={setServerId}
              options={zones.map((z) => ({ value: z.serverId, label: `${z.name}(区${z.serverId})` }))}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div style={{ marginBottom: 4 }}>
              <Text strong>累充上限</Text>{' '}
              <Text type="secondary">(可选,命中累充 ≤ 此值)</Text>
            </div>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              placeholder="留空则不按累充过滤"
              value={rechargeMax}
              onChange={setRechargeMax}
            />
          </Col>
          <Col xs={12} sm={12} md={8}>
            <div style={{ marginBottom: 4 }}>
              <Text strong>累计消耗AOZ币(最小)</Text>{' '}
              <Text type="secondary">(可选,整数)</Text>
            </div>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={0}
              step={1}
              placeholder="留空则不限下限"
              value={aozConsumeMin}
              onChange={setAozConsumeMin}
            />
          </Col>
          <Col xs={12} sm={12} md={8}>
            <div style={{ marginBottom: 4 }}>
              <Text strong>累计消耗AOZ币(最大)</Text>{' '}
              <Text type="secondary">(可选,整数)</Text>
            </div>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={0}
              step={1}
              placeholder="留空则不限上限"
              value={aozConsumeMax}
              onChange={setAozConsumeMax}
            />
          </Col>
          <Col xs={24}>
            <Text type="secondary">
              说明:累计消耗AOZ币为每日凌晨定时统计(非实时),统计口径为近30天窗口。
            </Text>
          </Col>
        </Row>

        <Divider orientation="left" style={{ marginTop: 20 }}>道具数量阈值(多条为交集,同时满足)</Divider>

        {thresholds.map((row, index) => (
          <Row gutter={[12, 12]} key={row.key} align="middle" style={{ marginBottom: 10 }}>
            <Col xs={24} sm={10} md={9}>
              <Select
                style={{ width: '100%' }}
                showSearch
                allowClear
                placeholder="选择道具 / 搜索名称"
                value={row.gid}
                filterOption={false}
                loading={searching}
                onSearch={searchItems}
                onChange={(val) => updateRow(row.key, { gid: val })}
                notFoundContent={searching ? '搜索中...' : '输入关键词搜索'}
              >
                <OptGroup label="置顶(三币)">
                  {PINNED_ITEMS.map((it) => (
                    <Option key={`pin-${it.gid}`} value={it.gid}>
                      {it.name}
                      <Text type="secondary"> #{it.gid}</Text>
                      {it.scope === 'global'
                        ? <Tag color="orange" style={{ marginLeft: 6 }}>全局</Tag>
                        : <Tag color="blue" style={{ marginLeft: 6 }}>背包</Tag>}
                    </Option>
                  ))}
                </OptGroup>
                {itemOptions.length > 0 && (
                  <OptGroup label="搜索结果">
                    {itemOptions.map((it) => (
                      <Option key={`item-${it.gid}`} value={it.gid}>
                        {it.name}<Text type="secondary"> #{it.gid}</Text>
                      </Option>
                    ))}
                  </OptGroup>
                )}
              </Select>
            </Col>
            <Col xs={12} sm={5} md={4}>
              <InputNumber
                style={{ width: '100%' }}
                placeholder="gid(可手填)"
                min={1}
                value={row.gid}
                onChange={(val) => updateRow(row.key, { gid: val })}
              />
            </Col>
            <Col xs={12} sm={4} md={4}>
              <InputNumber
                style={{ width: '100%' }}
                placeholder="等级(可选)"
                min={0}
                value={row.level}
                onChange={(val) => updateRow(row.key, { level: val })}
              />
            </Col>
            <Col xs={16} sm={4} md={4}>
              <InputNumber
                style={{ width: '100%' }}
                placeholder="数量≥"
                min={1}
                value={row.min}
                onChange={(val) => updateRow(row.key, { min: val })}
              />
            </Col>
            <Col xs={8} sm={1} md={3}>
              <Button
                danger
                icon={<DeleteOutlined />}
                disabled={thresholds.length <= 1}
                onClick={() => removeRow(row.key)}
              />
            </Col>
          </Row>
        ))}

        <Button type="dashed" icon={<PlusOutlined />} onClick={addRow} style={{ marginTop: 4 }}>
          添加阈值行
        </Button>

        {hasGlobalSelected && (
          <Alert
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            style={{ marginTop: 12 }}
            message="已选择全局资源(如 AOZ / 黄金 / 幸运币-全局),将按 user_info 资源列统计。"
          />
        )}

        <Divider />
        <Space>
          <Button type="primary" icon={<SearchOutlined />} loading={loading} onClick={runScan}>
            开始扫描
          </Button>
          <Button
            icon={<DownloadOutlined />}
            disabled={!result || !result.results || result.results.length === 0}
            onClick={exportCsv}
          >
            导出 CSV
          </Button>
        </Space>
      </Card>

      {result && (
        <Card
          title="扫描结果"
          extra={
            <Space size="large">
              <Statistic title="候选" value={result.candidate_count} valueStyle={{ fontSize: 16 }} />
              <Statistic title="命中" value={result.total} valueStyle={{ fontSize: 16, color: '#cf1322' }} />
            </Space>
          }
        >
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            累计消耗AOZ币{result.aoz_rollup_date ? `统计截至 ${result.aoz_rollup_date}` : '为定时统计结果'}:每日凌晨定时统计(非实时),统计口径为近30天窗口。
          </Text>
          {result.truncated && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={`命中 ${result.total} 条,已截断展示前 ${result.max_results} 条,请缩小范围或导出后分批处理。`}
            />
          )}
          <Table
            rowKey="user_id"
            size="small"
            columns={resultColumns}
            dataSource={result.results}
            scroll={{ x: 'max-content' }}
            pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (t) => `共 ${t} 条` }}
          />
        </Card>
      )}
    </div>
  );
}

export default CheatAccountScan;

